import { partition, uniq } from 'lodash'
import { computed, makeObservable } from 'mobx'

import RestDay, { RestDayPayload } from 'modules/shift_management/store/internal/models/RestDay'

class RestDays {
  restDays: RestDay[]

  constructor(restDays: RestDay[]) {
    this.restDays = restDays

    makeObservable(this, {
      employeeIds: computed,
      restDaysIds: computed,
      restDaysLength: computed,
    })
  }

  /**
   * Transforms an array of objects into an array of RestDay objects
   */
  static build(restDays: RestDayPayload[]): RestDays {
    const restDayObjects = restDays.map((restDay) => {
      return RestDay.build(restDay)
    })

    return new RestDays(restDayObjects)
  }

  get restDaysLength(): number {
    return this.restDays.length
  }

  filterByDate(date: moment.Moment): RestDay[] {
    return this.restDays.filter((restDay) => restDay.isDate(date))
  }

  filterByRange(firstDay: moment.Moment, lastDay: moment.Moment): RestDays {
    const restDays = this.restDays.filter((restDay: RestDay) => restDay.inRange(firstDay, lastDay))

    return new RestDays(restDays)
  }

  get restDaysIds(): number[] {
    return this.restDays.map((s) => s.id)
  }

  get employeeIds(): number[] {
    return uniq(this.restDays.map((s) => s.employeeId))
  }

  addRestDays(days: RestDay[]): void {
    this.restDays.push(...days)
  }

  destroyRestDay(id: number): void {
    const index = this.restDays.findIndex((restDay) => restDay.id === id)
    if (index >= 0) this.restDays.splice(index, 1)
  }

  destroyRestDays(ids: number[]): void {
    const idsSet = new Set(ids)
    const [restDaysToDestroy, restDaysToAdd] = partition(this.restDays, (r: RestDay) =>
      idsSet.has(r.id)
    )

    if (restDaysToDestroy.length === ids.length) {
      this.restDays = restDaysToAdd
    }
  }

  filterByDraft(): RestDay[] {
    return this.restDays.filter((day: RestDay) => day.isDraft)
  }

  filterPublishedByDate(date: moment.Moment): RestDay[] {
    return this.restDays.filter((day: RestDay) => day.isPublished && day.isDate(date))
  }

  filterByNonEmployeeIds(employeeIds: number[]): RestDay[] {
    const set = new Set(employeeIds)
    return this.restDays.filter((restDay: RestDay) => !set.has(restDay.employeeId))
  }
}

export default RestDays
